import React from 'react';
import { useTracking } from 'react-tracking';
import InfoTooltip from 'utils/components/info-tooltip';
import Icon from 'utils/components/icon';
import {
  IAdvancedFilters,
  Message,
  MessageStatus,
  Post,
  SpotPolicy,
  User,
  ViewType
} from '../../../state/types';
import MessageActionsDropdown from './message-actions-dropdown';
import classNames from 'classnames';
import { ApproveIcon, PublishedIcon, RejectIcon } from 'app/scripts/icons';

interface Props {
  message: Message;
  spotPolicy: SpotPolicy;
  ip?: string;
  user?: User;
  conversation?: Post;
  viewType?: ViewType;
  approveItem: (message, options?) => void;
  rejectItem: (message, options?) => void;
  navigateToConversation: () => void;
  openAddToBlackListModal: () => void;
  changePageFilters: ({ filters }: { filters: Partial<IAdvancedFilters> }) => void;
  setConversationPolicy: (props) => void;
  openAuditLogModal: () => void;
  isModerationButtonEnabled: boolean;
}

const MessageActions: React.FC<Props> = ({
  message,
  approveItem,
  rejectItem,
  spotPolicy,
  ip,
  user,
  conversation,
  viewType,
  navigateToConversation = () => {},
  openAddToBlackListModal = () => {},
  changePageFilters = () => {},
  setConversationPolicy = () => {},
  openAuditLogModal = () => {},
  isModerationButtonEnabled
}) => {
  const status = message.get('status');
  const isDeleted = status === MessageStatus.DELETED;
  const isProcessing: boolean = message.get('isProcessing');
  const showActionButton = isModerationButtonEnabled && !isProcessing;
  const tracking = useTracking();

  const onApproveClick = () => {
    tracking.trackEvent({ eventName: 'message approved clicked' });
    approveItem(message);
  };
  const onRejectClick = () => {
    tracking.trackEvent({ eventName: 'message rejected clicked' });
    rejectItem(message);
  };

  const publishedIcon = message.get('published') && (
    <span className="action published">
      <span className="tooltip-wrapper">
        <InfoTooltip direction="top">
          <span className="text">Comment is published</span>
        </InfoTooltip>
        <PublishedIcon className="published-icon" />
      </span>
    </span>
  );

  const deletedIcon = isDeleted && (
    <span className="action published">
      <span className="tooltip-wrapper">
        <InfoTooltip direction="top">
          <span className="text">Comment is deleted</span>
        </InfoTooltip>
        <Icon name="deleted-label" className="deleted-icon" />
      </span>
    </span>
  );

  const disabledClassName = { disabled: isDeleted };
  const approved =
    status === MessageStatus.APPROVED ? (
      <span className={'action action-approve approved'}>{'Approved'}</span>
    ) : (
      showActionButton && (
        <span
          className={classNames('action', 'action-approve', disabledClassName)}
          onClick={onApproveClick}
        >
          <ApproveIcon className={classNames('approve-icon', disabledClassName)} />
          {'Approve'}
        </span>
      )
    );

  const blocked =
    status === MessageStatus.BLOCKED ? (
      <span className={'action action-block blocked'}>{'Rejected'}</span>
    ) : (
      showActionButton && (
        <span
          className={classNames('action', 'action-block', disabledClassName)}
          onClick={onRejectClick}
        >
          <RejectIcon className={classNames('block-icon', disabledClassName)} />
          {'Reject'}
        </span>
      )
    );

  const processing = isProcessing && isModerationButtonEnabled && (
    <span className="processing">
      Processing
      <span className="spinner" />
    </span>
  );

  return (
    <div className={'actions'}>
      <div className={'actions-wrapper'}>
        {processing}
        {deletedIcon}
        {publishedIcon}
        {approved}
        {blocked}
        <MessageActionsDropdown
          spotPolicy={spotPolicy}
          ip={ip}
          user={user}
          conversation={conversation}
          viewType={viewType}
          navigateToConversation={navigateToConversation}
          openAddToBlackListModal={openAddToBlackListModal}
          changePageFilters={changePageFilters}
          setConversationPolicy={setConversationPolicy}
          openAuditLogModal={openAuditLogModal}
          messageId={message.get('id')}
          isModerationButtonEnabled={isModerationButtonEnabled}
        />
      </div>
    </div>
  );
};

export default MessageActions;
